import { useEffect, useRef } from "react";

// Detect if the app is running inside the Shopify embed iframe.
export const useIsEmbedded = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("embed") === "1" || (window.self !== window.top);
};

// Continuously post the document height to the parent so the iframe auto-resizes.
export const useEmbedResize = (enabled) => {
  const last = useRef(0);
  useEffect(() => {
    if (!enabled) return;
    const send = () => {
      const h = Math.ceil(document.documentElement.scrollHeight);
      if (h !== last.current) {
        last.current = h;
        window.parent?.postMessage({ source: "cd-paddle-finder", type: "resize", height: h }, "*");
      }
    };
    send();
    const ro = new ResizeObserver(send);
    ro.observe(document.body);
    const iv = setInterval(send, 800);
    return () => {
      ro.disconnect();
      clearInterval(iv);
    };
  }, [enabled]);
};

// Read theme tokens from the URL and apply them to CSS variables at :root.
// Merchants pass these via <script data-primary="#..." data-font="..." /> and
// embed.js forwards them into the iframe's URL. This keeps the widget in sync
// with the host Shopify theme without any build step.
const THEME_MAP = {
  primary: "--cd-primary",
  accent: "--cd-accent",
  ink: "--cd-ink",
  surface: "--cd-surface",
  bg: "--cd-bg",
  rcard: "--cd-radius-card",
  rbtn: "--cd-radius-btn",
};

// Derive a slightly-darker hover from a hex primary.
const darkenHex = (hex, amount = 0.12) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  const [r, g, b] = [1, 2, 3].map((i) => Math.max(0, Math.round(parseInt(m[i], 16) * (1 - amount))));
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
};

const softHex = (hex, alpha = 0.12) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  const [r, g, b] = [1, 2, 3].map((i) => parseInt(m[i], 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const useThemeSync = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const root = document.documentElement;

    Object.entries(THEME_MAP).forEach(([key, cssVar]) => {
      const v = params.get(key);
      if (v) root.style.setProperty(cssVar, v);
    });

    const primary = params.get("primary");
    if (primary) {
      const hover = darkenHex(primary, 0.12);
      const soft = softHex(primary, 0.1);
      if (hover) root.style.setProperty("--cd-primary-hover", hover);
      if (soft) root.style.setProperty("--cd-primary-soft", soft);
    }
    const accent = params.get("accent");
    if (accent) {
      const soft = softHex(accent, 0.16);
      if (soft) root.style.setProperty("--cd-accent-soft", soft);
    }

    const font = params.get("font");
    if (font) {
      document.body.style.fontFamily = font;
      root.style.setProperty("--cd-font", font);
    }

    // Store shop domain for cart permalinks (used by quizConfig helper via window)
    const shop = params.get("shop");
    if (shop) {
      window.__CD_SHOP_DOMAIN__ = shop.startsWith("http") ? shop : `https://${shop}`;
    }
  }, []);
};
